<template>
  <article
    class="v2-enrollment-card v2-card"
    :class="{ 'v2-enrollment-card--live': isLive, 'v2-enrollment-card--past': isPast }"
  >
    <EventCard :event="event" />

    <footer class="v2-enrollment-card__footer">
      <div class="v2-enrollment-card__status">
        <StatusBadge :label="status.label" :variant="status.variant" />
        <span v-if="ticketName" class="v2-enrollment-card__ticket">
          <v-icon size="14">{{ modalityIcon }}</v-icon>
          <span>{{ ticketName }}</span>
        </span>
      </div>

      <div class="v2-enrollment-card__actions">
        <BaseButton
          v-if="canJoinZoom"
          variant="amber"
          size="sm"
          icon="mdi-video"
          :loading="joiningZoom"
          :disabled="!isLive && !zoomJoinUrl"
          @click="onJoinZoom"
        >
          {{ isLive ? "Unirse ahora" : "Unirse a Zoom" }}
        </BaseButton>

        <BaseButton
          v-if="canShowQr"
          variant="primary"
          size="sm"
          icon="mdi-qrcode"
          @click="onOpenQr"
        >
          Ver QR
        </BaseButton>

        <BaseButton
          v-if="canShowCertificate"
          variant="ghost"
          size="sm"
          icon="mdi-certificate-outline"
          :loading="downloadingCertificate"
          @click="onDownloadCertificate"
        >
          {{ diplomaUrl ? "Ver constancia" : "Solicitar constancia" }}
        </BaseButton>
      </div>
    </footer>

    <div v-if="location" class="v2-enrollment-card__location">
      <v-icon size="14">mdi-map-marker-outline</v-icon>
      <span>{{ location }}</span>
    </div>

    <v-dialog v-model="qrDialog" max-width="420" scrollable>
      <div v-if="activeItem" class="v2-enrollment-card__qr-dialog">
        <header class="v2-enrollment-card__qr-head">
          <h3 class="v2-headline v2-headline--display-sm">
            {{ activeItem.event && activeItem.event.name }}
          </h3>
          <p v-if="eventStart" class="v2-body v2-body--sm v2-body--muted">
            {{ formatEventDate(activeItem) }}
          </p>
        </header>
        <div class="v2-enrollment-card__qr-body">
          <img
            :src="qrUrl"
            :alt="`Código QR para ${activeItem.event && activeItem.event.name}`"
            class="v2-enrollment-card__qr-image"
            width="240"
            height="240"
          />
          <p class="v2-body v2-body--sm v2-body--muted">
            Presenta este código en la entrada del evento.
          </p>
          <p v-if="location" class="v2-enrollment-card__qr-location">
            <v-icon size="14">mdi-map-marker</v-icon>
            {{ location }}
          </p>
        </div>
        <footer class="v2-enrollment-card__qr-foot">
          <BaseButton variant="primary" @click="qrDialog = false">Cerrar</BaseButton>
        </footer>
      </div>
    </v-dialog>
  </article>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import StatusBadge from "@/components/memberDashboardV2/ui/StatusBadge.vue";
import EventCard from "./EventCard.vue";
import {
  resolveEnrollmentStatus,
  isEnrollmentLive,
  isEnrollmentPast,
  hasZoom,
  isPresential,
  getEventModality,
  hasDiploma,
  getDiplomaUrl,
  getZoomJoinUrl,
} from "./enrollmentHelpers";
import { getEventStartMoment, formatEventDateFull } from "./eventsHelpers";
import moment from "moment-timezone";

const MODALITY_ICON = {
  PRESENTIAL: "mdi-map-marker",
  VIRTUAL: "mdi-monitor",
  HYBRID: "mdi-earth",
};

export default {
  name: "EnrollmentCard",
  components: { BaseButton, StatusBadge, EventCard },
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      qrDialog: false,
      activeQrItem: null,
      joiningZoom: false,
      downloadingCertificate: false,
    };
  },
  computed: {
    event() {
      return this.item && this.item.event ? this.item.event : this.item;
    },
    eventStart() {
      return getEventStartMoment(this.item);
    },
    status() {
      return resolveEnrollmentStatus(this.item);
    },
    isLive() {
      return isEnrollmentLive(this.item);
    },
    isPast() {
      return isEnrollmentPast(this.item);
    },
    ticketName() {
      return this.item && this.item.ticket && this.item.ticket.name;
    },
    modality() {
      return getEventModality(this.item);
    },
    modalityIcon() {
      return MODALITY_ICON[this.modality] || "mdi-calendar";
    },
    location() {
      const ev = this.event;
      if (!ev) return null;
      if (ev.presentialEventConfiguration && ev.presentialEventConfiguration.formattedAddress) {
        return ev.presentialEventConfiguration.formattedAddress;
      }
      if (ev.place) return ev.place;
      if (ev.location && ev.location.name) return ev.location.name;
      return null;
    },
    canJoinZoom() {
      return hasZoom(this.item) && Boolean(getZoomJoinUrl(this.item) || this.isLive);
    },
    zoomJoinUrl() {
      return getZoomJoinUrl(this.item);
    },
    canShowQr() {
      return isPresential(this.item) && this.item._id;
    },
    canShowCertificate() {
      return hasDiploma(this.item) && this.isPast;
    },
    diplomaUrl() {
      return getDiplomaUrl(this.item);
    },
    qrUrl() {
      if (!this.activeQrItem) return "";
      const id = this.activeQrItem._id || "";
      return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(id)}`;
    },
  },
  methods: {
    ...mapActions("memberEvents", ["fetchZoomJoinLink"]),
    ...mapMutations("notification", ["show"]),
    formatEventDate: formatEventDateFull,
    onOpenQr() {
      this.activeQrItem = this.item;
      this.qrDialog = true;
    },
    async onJoinZoom() {
      const eventId = this.event && this.event._id;
      if (!eventId) return;
      this.joiningZoom = true;
      try {
        const result = await this.fetchZoomJoinLink(eventId);
        if (result && result.status && result.joinUrl) {
          window.open(result.joinUrl, "_blank", "noopener,noreferrer");
        } else {
          this.show({
            text: (result && result.message) || "No se pudo obtener el enlace de Zoom",
            color: "error",
          });
        }
      } catch (e) {
        this.show({
          text: "Error al obtener el enlace de Zoom",
          color: "error",
        });
      } finally {
        this.joiningZoom = false;
      }
    },
    async onDownloadCertificate() {
      if (this.diplomaUrl) {
        window.open(this.diplomaUrl, "_blank", "noopener,noreferrer");
        return;
      }
      this.downloadingCertificate = true;
      try {
        const eventId = this.event && this.event._id;
        if (!eventId) return;
        await Vue.axios({
          method: "POST",
          url: `/events/send-diplomas/${eventId}`,
        });
        this.show({
          text: "Te enviamos tu constancia por correo en unos minutos.",
          color: "success",
        });
      } catch (e) {
        this.show({
          text: "No se pudo procesar la constancia. Intenta más tarde.",
          color: "error",
        });
      } finally {
        this.downloadingCertificate = false;
      }
    },
  },
};
</script>

<style scoped>
.v2-enrollment-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.v2-enrollment-card :deep(.v2-event-card) {
  flex: 1 1 auto;
}

.v2-enrollment-card--past :deep(.v2-event-card) {
  opacity: 0.85;
}

.v2-enrollment-card__footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4) var(--space-4);
  border-top: 1px solid var(--color-border);
  background-color: var(--color-surface-low);
  border-radius: 0 0 var(--radius-xl) var(--radius-xl);
}

.v2-enrollment-card__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.v2-enrollment-card__ticket {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
}

.v2-enrollment-card__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.v2-enrollment-card__actions > * {
  flex: 1 1 auto;
}

.v2-enrollment-card__location {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-4) var(--space-3);
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  background-color: var(--color-surface-low);
}

.v2-enrollment-card__location :first-child {
  flex-shrink: 0;
}

.v2-enrollment-card__location span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-enrollment-card__qr-dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  background-color: var(--color-surface);
  border-radius: var(--radius-xl);
}

.v2-enrollment-card__qr-head h3 {
  margin: 0 0 var(--space-1) 0;
}

.v2-enrollment-card__qr-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  text-align: center;
}

.v2-enrollment-card__qr-image {
  display: block;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-2);
  background-color: var(--color-surface-low);
}

.v2-enrollment-card__qr-location {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
}

.v2-enrollment-card__qr-foot {
  display: flex;
  justify-content: flex-end;
}
</style>
